/**
 * Les icônes des entrées du menu qui ne sont pas des catégories de dossier.
 *
 * `Picto` (`ui/Picto.tsx`) porte le langage graphique de la marque — deux teintes,
 * détail creusé en négatif — réservé aux six catégories du dossier. Le mêler à des
 * entrées de nature différente (le compte, la sortie) aurait dilué ce qui le rend
 * reconnaissable. Ces icônes-ci reprennent plutôt le dessin de celle d'Urgence
 * (`MenuUrgence.tsx`) : un aplat simple, `currentColor`, sans second ton — le
 * vocabulaire des gestes du menu, pas celui des écrans du dossier.
 */

export function IconeDossiers() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M3 6.5A2.5 2.5 0 0 1 5.5 4h4.17a1 1 0 0 1 .78.37l1.6 1.63H18.5A2.5 2.5 0 0 1 21 8.5v9A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5Z"
      />
    </svg>
  );
}

/**
 * Le même dossier, avec une croix creusée dedans.
 *
 * Elle est **découpée et non posée** — un `fillRule="evenodd"` sur un seul tracé, comme
 * le détachement du logo et des galets. Une croix peinte par-dessus aurait figé la
 * couleur du panneau dans l'icône, et le menu n'a pas le même fond en clair et en sombre.
 *
 * La forme reste celle de `IconeDossiers` à dessein : l'entrée voisine y mène, celle-ci
 * en ouvre un — deux dessins sans rapport auraient fait de la création autre chose qu'un
 * dossier de plus.
 */
export function IconeNouveauDossier() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M3 6.5A2.5 2.5 0 0 1 5.5 4h4.17a1 1 0 0 1 .78.37l1.6 1.63H18.5A2.5 2.5 0 0 1 21 8.5v9A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5ZM10.8 9.7h2.4v2.6h2.6v2.4h-2.6v2.6h-2.4v-2.6H8.2v-2.4h2.6Z"
      />
    </svg>
  );
}

export function IconeCompte() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="3.6" fill="currentColor" />
      <path
        fill="currentColor"
        d="M4.5 19c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1Z"
      />
    </svg>
  );
}

/** Un rouage à six dents : un anneau creusé (`fillRule="evenodd"`), les dents en rectangles
 *  répétés par rotation plutôt que redessinés un à un — le seul moyen d'être sûr qu'ils
 *  sont identiques. */
export function IconeOptions() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <g fill="currentColor">
        <path
          fillRule="evenodd"
          d="M12 16.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4Zm0-1.8a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8Z"
        />
        <rect x="10.8" y="2.4" width="2.4" height="3.6" rx="1.2" />
        <rect x="10.8" y="2.4" width="2.4" height="3.6" rx="1.2" transform="rotate(60 12 12)" />
        <rect x="10.8" y="2.4" width="2.4" height="3.6" rx="1.2" transform="rotate(120 12 12)" />
        <rect x="10.8" y="2.4" width="2.4" height="3.6" rx="1.2" transform="rotate(180 12 12)" />
        <rect x="10.8" y="2.4" width="2.4" height="3.6" rx="1.2" transform="rotate(240 12 12)" />
        <rect x="10.8" y="2.4" width="2.4" height="3.6" rx="1.2" transform="rotate(300 12 12)" />
      </g>
    </svg>
  );
}

/** Un cadre de porte, et la flèche qui en sort. */
export function IconeSortie() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M9 4.5A1.5 1.5 0 0 1 10.5 3H18a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3h-7.5A1.5 1.5 0 0 1 9 19.5V17a1 1 0 1 1 2 0v2.5a.5.5 0 0 0 .5.5H18a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-6.5a.5.5 0 0 0-.5.5V7a1 1 0 1 1-2 0Z"
      />
      <path
        fill="currentColor"
        d="M13.7 11.3a1 1 0 0 1 0 1.4l-2.3 2.3a1 1 0 0 1-1.7-.7v-1.3H3a1 1 0 1 1 0-2h6.7v-1.3a1 1 0 0 1 1.7-.7Z"
      />
    </svg>
  );
}
